// contexts/GrupoContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchData, postData, putData, deleteData } from '../../services/Api';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';

const GrupoContext = createContext();

export const useGrupo = () => useContext(GrupoContext);

const normalizeCashBackNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const number = Number(value);
    return number > 0 ? number : null;
};

const buildGrupoPayload = (grupo) => ({
    id: Number(grupo?.id) || 0,
    descricao: grupo?.descricao?.trim() || '',
    vendeApp: !!grupo?.vendeApp,
    inativo: !!grupo?.inativo,
    imagemGrupoId: grupo?.imagemGrupoId || grupo?.imagemGrupo?.id || null,
    imagemGrupo: grupo?.imagemGrupo || null,
    orderNum: grupo?.orderNum === '' || grupo?.orderNum === null || grupo?.orderNum === undefined
        ? null
        : Number(grupo.orderNum),
    cashBackPercent: normalizeCashBackNumber(grupo?.cashBackPercent),
});

const extractErrorMessage = (error) => {
    if (error?.errors && typeof error.errors === 'object') {
        return Object.entries(error.errors)
            .flatMap(([field, messages]) => {
                const list = Array.isArray(messages) ? messages : [messages];
                return list.map((message) => `${field}: ${message}`);
            })
            .join('\n');
    }

    return error?.title || error?.message || String(error);
};

const shouldRetryWithWrapper = (error) => {
    const grupoErrors = error?.errors?.grupo;
    return Array.isArray(grupoErrors)
        && grupoErrors.some((message) => String(message).toLowerCase().includes('required'));
};

const saveGrupo = async (method, grupo) => {
    const payload = buildGrupoPayload(grupo);

    try {
        return await method('/Grupo', payload);
    } catch (error) {
        if (!shouldRetryWithWrapper(error)) {
            throw error;
        }

        return method('/Grupo', { grupo: payload });
    }
};

export const GrupoProvider = ({ children }) => {
    const [grupos, setGrupos] = useState([]);
    const { showAlert } = useAlert();
    const { showMessage } = useMessage();

    const fetchGrupos = useCallback(async () => {
        try {
            const result = await fetchData('/Grupo');
            setGrupos(result);
        } catch (error) {
            console.error('Erro ao buscar grupos:', error);
            showMessage(`Erro ao buscar grupos! ${error?.message || error}`, 'danger');
        }
    }, [showMessage]);

    useEffect(() => {
        fetchGrupos();
    }, [fetchGrupos]);

    const addGrupo = async (grupo) => {
        try {
            const result = await saveGrupo(postData, grupo);
            setGrupos((currentGrupos) => [...currentGrupos, result]);
            showAlert('Grupo adicionado com sucesso!');
        } catch (error) {
            console.error('Erro ao adicionar grupo:', error);
            showMessage(`Erro ao adicionar grupo!\n${extractErrorMessage(error)}`, 'danger');
            throw error;
        }
    };

    const updateGrupo = async (grupo) => {
        try {
            const result = await saveGrupo(putData, grupo);
            setGrupos((currentGrupos) =>
                currentGrupos.map((item) => (item.id === result.id ? result : item)),
            );
            showAlert('Grupo alterado com sucesso!');
        } catch (error) {
            console.error('Erro ao atualizar grupo:', error);
            showMessage(`Erro ao alterar grupo!\n${extractErrorMessage(error)}`, 'danger');
            throw error;
        }
    };

    const updateGrupoOrderNum = async (grupoId, orderNum) => {
        try {
            const grupoAtual = grupos.find((item) => item.id === grupoId);
            if (!grupoAtual) {
                return null;
            }

            const result = await saveGrupo(putData, {
                ...grupoAtual,
                orderNum,
            });

            setGrupos((currentGrupos) =>
                currentGrupos.map((item) => (item.id === result.id ? result : item)),
            );
            showAlert('Ordem alterada com sucesso!');
            return result;
        } catch (error) {
            console.error('Erro ao atualizar ordem do grupo:', error);
            showMessage(`Erro ao alterar ordem do grupo!\n${extractErrorMessage(error)}`, 'danger');
            return null;
        }
    };

    const deleteGrupo = async (id) => {
        try {
            await deleteData('/Grupo', { grupoId: id });
            setGrupos((currentGrupos) => currentGrupos.filter((grupo) => grupo.id !== id));
            showAlert('Grupo excluido com sucesso!');
        } catch (error) {
            console.error('Erro ao excluir grupo:', error);
            showMessage('Erro ao excluir grupo!', 'danger');
        }
    };

    return (
        <GrupoContext.Provider value={{ grupos, addGrupo, updateGrupo, updateGrupoOrderNum, deleteGrupo }}>
            {children}
        </GrupoContext.Provider>
    );
};
